/**
 * Browser side of the language suggestion (logic in langSuggest.ts) and of the header's
 * language menu. Injected into every
 * indexable site page by seoHead() in src/blog/vitePlugin.ts. No redirect, no cookie:
 * a dismissal lives in localStorage, and a page with storage blocked simply shows the
 * banner again next time.
 */
import { SITE } from './config.js';
import { isLang } from './seo.js';
import { bannerCopy, LANG_BANNER_DISMISSED_KEY, sitePath, suggestLanguage } from './langSuggest.js';

function dismissed(): boolean {
  try {
    return localStorage.getItem(LANG_BANNER_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function remember(): void {
  try {
    localStorage.setItem(LANG_BANNER_DISMISSED_KEY, '1');
  } catch {
    // Storage blocked (private mode): the banner comes back on the next page, that's all.
  }
}

function show(): void {
  if (dismissed()) return;
  const htmlLang = document.documentElement.lang;
  const pageLang = isLang(htmlLang) ? htmlLang : 'es';
  const alternates: Record<string, string> = {};
  for (const l of document.querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]')) {
    alternates[l.hreflang] = l.getAttribute('href') ?? '';
  }
  const s = suggestLanguage(pageLang, navigator.languages ?? [navigator.language], alternates);
  if (!s) return;
  const copy = bannerCopy(s);

  const bar = document.createElement('aside');
  bar.className = 'lang-banner';
  bar.lang = s.lang;
  bar.setAttribute('aria-label', copy.text);
  const text = document.createElement('p');
  text.textContent = copy.text;
  const go = document.createElement('a');
  go.href = sitePath(s.href, SITE.ORIGIN);
  go.hreflang = s.lang;
  go.textContent = copy.action;
  go.className = 'lang-banner-go';
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'lang-banner-close';
  close.setAttribute('aria-label', copy.close);
  close.textContent = '×';
  close.addEventListener('click', () => {
    remember();
    bar.remove();
  });
  bar.append(text, go, close);
  document.body.prepend(bar);
}

/** The header's language menu is a <details>: close it on a click outside it or on Escape. */
function wireLangMenus(): void {
  const menus = [...document.querySelectorAll<HTMLDetailsElement>('details.lang-menu')];
  if (!menus.length) return;
  document.addEventListener('click', (e) => {
    for (const m of menus) if (m.open && !m.contains(e.target as Node)) m.open = false;
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    for (const m of menus) {
      if (!m.open) continue;
      m.open = false;
      m.querySelector('summary')?.focus();
    }
  });
}

show();
wireLangMenus();
